import React, { useState } from 'react';
import { CloseIcon } from './Icons';
import { saveAppointment } from '../utils/firebaseService';

const defaultBookingData = {
  date: '',
  time: '',
  name: '',
  phone: '',
  email: '',
};

export default function BookingDrawer({
  isOpen,
  onClose,
  bookingStep: propBookingStep,
  setBookingStep: propSetBookingStep,
  bookingForm: propBookingForm,
  setBookingForm: propSetBookingForm,
}) {
  const [internalForm, setInternalForm] = useState(defaultBookingData);
  const [internalStep, setInternalStep] = useState(1);

  const bookingForm = propBookingForm || internalForm;
  const setBookingForm = propSetBookingForm || setInternalForm;
  const bookingStep = propBookingStep !== undefined ? propBookingStep : internalStep;
  const setBookingStep = propSetBookingStep || setInternalStep;

  const getLocalDateString = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getLocalTimeString = () => {
    const d = new Date();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Generates 1-hour slots starting from 09:30 to 19:30
  const generateTimeOptions = () => {
    const options = [];
    let startHour = 9;
    let startMin = 30;

    while (startHour < 19) {
      let endHour = startHour + 1;
      const hhStart = String(startHour).padStart(2, '0');
      const mmStart = String(startMin).padStart(2, '0');
      const timeVal = `${hhStart}:${mmStart}`;

      const isPmStart = startHour >= 12;
      const displayHourStart = startHour % 12 === 0 ? 12 : startHour % 12;
      const labelStart = `${String(displayHourStart).padStart(2, '0')}:${mmStart} ${isPmStart ? 'PM' : 'AM'}`;

      const isPmEnd = endHour >= 12;
      const displayHourEnd = endHour % 12 === 0 ? 12 : endHour % 12;
      const labelEnd = `${String(displayHourEnd).padStart(2, '0')}:${mmStart} ${isPmEnd ? 'PM' : 'AM'}`;

      const optionLabel = `${labelStart} - ${labelEnd}`;
      options.push({ value: timeVal, label: optionLabel });

      startHour += 1;
    }
    return options;
  };

  // Filters out past slots if selected date is today
  const getAvailableTimeOptions = () => {
    const allOptions = generateTimeOptions();
    const todayStr = getLocalDateString();

    if (bookingForm?.date === todayStr) {
      const currentTime = getLocalTimeString();
      return allOptions.filter(opt => opt.value > currentTime);
    }
    return allOptions;
  };

  const formatTimeDisplay = (time24) => {
    if (!time24) return '';
    const [hoursStr, minutesStr] = time24.split(':');
    const startHour = parseInt(hoursStr, 10);
    const endHour = startHour + 1;

    const isPmStart = startHour >= 12;
    const displayHourStart = startHour % 12 === 0 ? 12 : startHour % 12;
    const labelStart = `${String(displayHourStart).padStart(2, '0')}:${minutesStr} ${isPmStart ? 'PM' : 'AM'}`;

    const isPmEnd = endHour >= 12;
    const displayHourEnd = endHour % 12 === 0 ? 12 : endHour % 12;
    const labelEnd = `${String(displayHourEnd).padStart(2, '0')}:${minutesStr} ${isPmEnd ? 'PM' : 'AM'}`;

    return `${labelStart} - ${labelEnd}`;
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    const todayStr = getLocalDateString();

    if (!bookingForm?.name || !bookingForm?.phone || !bookingForm?.date || !bookingForm?.time) {
      alert('Please fill in all required fields.');
      return;
    }

    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test((bookingForm?.phone || '').replace(/[\s-()]/g, ''))) {
      alert('Please enter a valid 10-digit phone number.');
      return;
    }

    if (bookingForm?.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(bookingForm.email)) {
        alert('Please enter a valid email address.');
        return;
      }
    }

    if (bookingForm?.date < todayStr) {
      alert('Please select a current or future date.');
      return;
    }

    if (bookingForm?.time < '09:30' || bookingForm?.time > '19:30') {
      alert('Please select a time between 09:30 and 19:30.');
      return;
    }

    const newBooking = {
      id: `HOK-RSVP-${Math.floor(1000 + Math.random() * 9000)}`,
      name: bookingForm?.name || '',
      phone: bookingForm?.phone || '',
      email: bookingForm?.email || '',
      date: bookingForm?.date || '',
      time: formatTimeDisplay(bookingForm?.time),
      timestamp: new Date().toISOString(),
      status: 'Pending'
    };

    try {
      await saveAppointment(newBooking);
    } catch (err) {
      console.warn("Firestore save failed, fell back to local storage.", err);
    }

    const existing = JSON.parse(localStorage.getItem('hok_appointments') || '[]');
    localStorage.setItem('hok_appointments', JSON.stringify([newBooking, ...existing]));
    window.dispatchEvent(new Event('hok_appointment_booked'));

    setBookingStep(2);
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 md:p-6 transition-opacity duration-300 pointer-events-none ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0'}`}>
      <div onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-md transition-opacity" />
      <div className={`relative w-full max-w-2xl md:max-w-3xl bg-noise shadow-2xl rounded-sm p-4 sm:p-6 md:p-8 transition-all duration-300 flex flex-col max-h-[94vh] overflow-y-auto z-10 ${isOpen ? 'scale-100 translate-y-0 opacity-100' : 'scale-95 translate-y-4 opacity-0'}`}>
        <div className="flex items-center justify-between border-b border-[#584738]/10 pb-3 sm:pb-4 mb-4 sm:mb-6">
          <h3 className="font-serif text-lg sm:text-xl tracking-widest font-semibold uppercase text-[#584738]">
            Book An Appointment
          </h3>
          <button onClick={onClose} className="p-2 -mr-2 text-hok-charcoal hover:text-[#584738] active:scale-90 transition-all focus:outline-none cursor-pointer" aria-label="Close Booking Panel">
            <CloseIcon />
          </button>
        </div>

        {bookingStep === 1 ? (
          <form onSubmit={handleBookingSubmit} className="flex-1 flex flex-col gap-4 sm:gap-5 text-left font-sans font-light">
            <p className="text-[12.5px] sm:text-[13.5px] text-[#584738]/80 leading-relaxed font-light mb-1">
              Schedule your private consultation and let us craft a piece that’s uniquely yours.
            </p>

            {/* Date & Time Side-by-Side */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] tracking-widest font-sans font-bold uppercase text-[#584738]/70">Preferred Date *</label>
                <input
                  type="date"
                  min={getLocalDateString()}
                  value={bookingForm?.date || ''}
                  onChange={(e) => setBookingForm({ ...bookingForm, date: e.target.value, time: '' })}
                  className="bg-transparent border border-[#584738]/20 px-3.5 py-3 text-sm focus:outline-none focus:border-hok-gold text-hok-charcoal font-sans"
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] tracking-widest font-sans font-bold uppercase text-[#584738]/70">Preferred Time *</label>
                <select
                  value={bookingForm?.time || ''}
                  onChange={(e) => setBookingForm({ ...bookingForm, time: e.target.value })}
                  className="bg-transparent border border-[#584738]/20 px-3.5 py-3 text-sm focus:outline-none focus:border-hok-gold text-hok-charcoal font-sans"
                  required
                >
                  <option value="">Select Time</option>
                  {getAvailableTimeOptions().map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="w-full h-[1px] bg-[#584738]/10 my-1"></div>

            {/* Personal Details in 2 Columns */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] tracking-widest font-sans font-bold uppercase text-[#584738]/70">Full Name *</label>
                <input
                  type="text"
                  placeholder="Enter your name"
                  value={bookingForm?.name || ''}
                  onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })}
                  className="bg-transparent border border-[#584738]/20 px-3.5 py-3 text-sm focus:outline-none focus:border-hok-gold text-hok-charcoal placeholder-hok-charcoal/30 font-serif"
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] tracking-widest font-sans font-bold uppercase text-[#584738]/70">Phone Contact *</label>
                <input
                  type="tel"
                  placeholder="Enter phone number"
                  value={bookingForm?.phone || ''}
                  onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })}
                  className="bg-transparent border border-[#584738]/20 px-3.5 py-3 text-sm focus:outline-none focus:border-hok-gold text-hok-charcoal placeholder-hok-charcoal/30 font-sans"
                  required
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] tracking-widest font-sans font-bold uppercase text-[#584738]/70">Email Address</label>
              <input
                type="email"
                placeholder="Enter email address"
                value={bookingForm?.email || ''}
                onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })}
                className="bg-transparent border border-[#584738]/20 px-3.5 py-3 text-sm focus:outline-none focus:border-hok-gold text-hok-charcoal placeholder-hok-charcoal/30 font-sans"
              />
            </div>

            <button
              type="submit"
              className="w-full bg-[#584738] text-white hover:bg-black py-4 text-xs tracking-widest font-sans font-bold uppercase transition-colors shadow-sm mt-4 focus:outline-none"
            >
              BOOK MY APPOINTMENT
            </button>
          </form>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-6 font-serif">
            {/* Elegant Circle Icon */}
            <div className="w-16 h-16 rounded-full bg-[#CEC1A8]/20 flex items-center justify-center text-[#584738] mb-6 border border-[#584738]/20 shadow-xs">
              <svg className="w-6 h-6 stroke-current" fill="none" viewBox="0 0 24 24" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            </div>

            <h4 className="text-2xl font-normal tracking-widest text-[#584738] uppercase">
              Request Received
            </h4>
            <p className="text-[13.5px] text-[#584738]/85 mt-4 leading-relaxed max-w-sm font-sans font-light">
              Thank you, <strong className="font-semibold">{bookingForm?.name || 'Patron'}</strong>. We look forward to crafting a piece that’s uniquely yours. Your private consultation is requested for <strong className="font-semibold">{bookingForm?.date || ''}</strong> during the <strong className="font-semibold">{formatTimeDisplay(bookingForm?.time)}</strong> session.
            </p>

            <div className="w-full bg-[#CEC1A8]/10 border border-[#584738]/10 p-5 rounded-md my-8 text-left max-w-sm relative overflow-hidden backdrop-blur-xs shadow-xs">
              <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-[#CEC1A8] via-[#584738] to-[#CEC1A8]" />
              <p className="text-[#584738]/50 text-[10px] uppercase font-sans font-bold tracking-widest mb-1">Appointment Code</p>
              <p className="font-serif font-semibold text-[#584738] text-lg tracking-wider">HOK-RSVP-7892</p>
              <div className="w-full h-[1px] bg-[#584738]/10 my-3" />
              <p className="text-[#584738]/70 text-[12px] leading-relaxed font-sans font-light">
                Our private concierge will contact you shortly to confirm your invitation and schedule details.
              </p>
            </div>

            <button
              onClick={onClose}
              className="border border-[#584738]/30 px-8 py-3 text-xs tracking-widest font-semibold hover:bg-[#584738] hover:text-white transition-colors uppercase font-sans focus:outline-none"
            >
              Return to Gallery
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
